import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Leaf, ShieldCheck, Truck, Sparkles } from "lucide-react";
import { api, resolveImg } from "../lib/api";
import { BRAND } from "../lib/constants";
import ProductCard from "../components/ProductCard";

const Hero = () => {
  const navigate = useNavigate();
  return (
    <section className="relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-10 items-center py-12 lg:py-20">
          <div className="animate-fade-up">
            <span className="inline-block text-xs font-semibold tracking-[0.2em] text-[#C5A059] uppercase mb-5">
              Aarmira SuperNaturals
            </span>
            <h1 className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl text-[#1C2B20] leading-[1.05]">
              Nature's Power.
              <br />
              <span className="text-[#1F4D2E]">Your Wellness.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#5A6B5D] max-w-md leading-relaxed">
              Pure, natural and effective Ayurvedic products inspired by nature and 5,000 years of traditional wisdom.
            </p>
            <button
              onClick={() => navigate("/shop")}
              data-testid="hero-shop-now-btn"
              className="mt-8 inline-flex items-center gap-2 bg-[#1F4D2E] hover:bg-[#163A22] text-white font-medium rounded-full px-8 py-3.5 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
            >
              Shop Now <ArrowRight size={18} />
            </button>
          </div>
          <div className="relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="absolute -inset-4 bg-[#1F4D2E]/5 rounded-[2.5rem] blur-xl" />
            <img
              src={BRAND.hero}
              alt="Aarmira Ayurvedic products"
              className="relative rounded-[2rem] w-full object-cover shadow-xl"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const TrustBar = () => {
  const items = [
    { icon: Leaf, label: "100% Natural" },
    { icon: ShieldCheck, label: "Lab Tested & Safe" },
    { icon: Truck, label: "Cash on Delivery" },
    { icon: Sparkles, label: "No Harmful Chemicals" },
  ];
  return (
    <div className="bg-[#1F4D2E]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 grid grid-cols-2 md:grid-cols-4 gap-4">
        {items.map((it, i) => (
          <div key={i} className="flex items-center justify-center gap-2.5 text-[#E7E1D3] text-sm font-medium">
            <it.icon size={18} className="text-[#C5A059]" />
            {it.label}
          </div>
        ))}
      </div>
    </div>
  );
};

const CategorySection = ({ categories }) => {
  const navigate = useNavigate();
  return (
    <section className="py-16 sm:py-20" id="categories">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-serif font-bold text-3xl sm:text-4xl text-[#1C2B20]">Shop by Category</h2>
          <p className="mt-3 text-[#5A6B5D]">Explore our herbal ranges, crafted for every ritual</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6">
          {categories.map((cat) => (
            <div key={cat.id} className="flex flex-col items-center text-center" data-testid={`category-item-${cat.id}`}>
              <div className="h-28 w-28 sm:h-32 sm:w-32 rounded-full bg-[#EFEAE0] ring-1 ring-[#E2DAD0] overflow-hidden flex items-center justify-center hover-lift">
                <img src={resolveImg(cat.image_url)} alt={cat.name} className="h-full w-full object-cover" />
              </div>
              <h3 className="mt-4 font-semibold text-sm text-[#1C2B20] min-h-[40px] flex items-center">{cat.name}</h3>
              <button
                onClick={() => navigate(`/shop?category=${cat.id}`)}
                data-testid={`category-explore-${cat.id}`}
                className="mt-1 text-xs font-semibold text-[#1F4D2E] border border-[#1F4D2E] rounded-full px-5 py-1.5 hover:bg-[#1F4D2E] hover:text-white transition-colors"
              >
                Explore
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const NatureBanner = () => (
  <section className="relative py-24 my-8">
    <div className="absolute inset-0">
      <img src={BRAND.bannerBg} alt="" className="h-full w-full object-cover" />
      <div className="absolute inset-0 bg-[#13331D]/75" />
    </div>
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <div className="inline-block border-y-2 border-[#C5A059] py-6 px-4">
        <h2 className="font-serif font-bold text-3xl sm:text-5xl text-white">Pure Ingredients. Real Wellness.</h2>
      </div>
      <p className="mt-6 text-[#E7E1D3] max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
        Rooted in ancient Ayurvedic tradition, crafted with wildcrafted herbs and cold-pressed botanical oils —
        nature's best, in every product.
      </p>
    </div>
  </section>
);

const Home = () => {
  const [bestsellers, setBestsellers] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { bestseller: true } }).then((r) => setBestsellers(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <Hero />
      <TrustBar />
      <CategorySection categories={categories} />
      <section className="py-4 sm:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-4 mb-12">
            <span className="h-px w-16 bg-[#C5A059]" />
            <h2 className="font-serif font-bold text-3xl sm:text-4xl text-[#1C2B20] text-center">Best Sellers</h2>
            <span className="h-px w-16 bg-[#C5A059]" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {bestsellers.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 text-[#1F4D2E] font-semibold border-b-2 border-[#1F4D2E] pb-1 hover:gap-3 transition-all"
            >
              View All Products <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
      <NatureBanner />
    </div>
  );
};

export default Home;
